import React from 'react';
import Footer from './Footer';

interface AboutPageProps {
  darkMode: boolean;
}

const AboutPage: React.FC<AboutPageProps> = ({ darkMode }) => {
  const theme = {
    bg: darkMode ? 'bg-[#1e1e1e]' : 'bg-gray-100',
    panelBg: darkMode ? 'bg-[#252526]' : 'bg-white',
    text: darkMode ? 'text-[#d4d4d4]' : 'text-gray-900',
    mutedText: darkMode ? 'text-[#858585]' : 'text-gray-500',
    label: darkMode ? 'text-[#bbbbbb]' : 'text-gray-700',
    border: darkMode ? 'border-[#3e3e42]' : 'border-gray-200',
    link: darkMode ? 'text-[#4a9eff] hover:text-[#6bb5ff]' : 'text-blue-600 hover:text-blue-700',
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} py-12 px-4`}>
      <div className={`max-w-4xl mx-auto ${theme.panelBg} rounded-lg shadow-lg p-8 ${theme.border} border`}>
        <h1 className={`text-3xl font-bold mb-6 ${theme.text}`}>About Proto Workbench</h1>

        <div className="prose prose-invert max-w-none space-y-6">
          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>What is Proto Workbench?</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Proto Workbench is a free, professional online tool designed to help developers work with Protocol Buffers (Protobuf) efficiently. 
              Whether you're debugging gRPC APIs, analyzing binary payloads, or designing schemas, our tool provides a fast, secure, and 
              user-friendly experience for compiling JSON to Protobuf binary and decoding binary data back to JSON.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Unlike many online Protobuf tools, our workbench processes all data <strong className="font-semibold">entirely in your browser</strong>, 
              ensuring that your sensitive schemas and payloads never leave your device. This makes it safe for working with production APIs, 
              internal protocols, and confidential data structures.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Key Features</h2>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li><strong>Schema Editor:</strong> Full-featured editor with line numbers, syntax highlighting, and Find & Replace (Ctrl+F)</li>
              <li><strong>Dual Mode:</strong> Switch between JSON to Binary (Encoder) and Binary to JSON (Decoder) workflows</li>
              <li><strong>Auto-Templates:</strong> Automatically generate JSON templates or Hex strings based on your selected Message type</li>
              <li><strong>Format Support:</strong> Handle Hexadecimal (spaced or continuous) and Base64 encoded binaries</li>
              <li><strong>Real-Time Validation:</strong> Instant syntax error checking for both <code>.proto</code> schemas and data inputs</li>
              <li><strong>Dark Mode:</strong> Comfortable viewing in both light and dark themes</li>
              <li><strong>Client-Side Processing:</strong> All compilation happens locally using protobuf.js</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Privacy & Security</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Privacy is at the core of our design. All Protobuf processing happens locally in your browser using the protobuf.js library. 
              We don't send your data to any server, store it, or log it. This means:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Your <code>.proto</code> schemas and data stay completely private</li>
              <li>Safe for sensitive information like API schemas and production payloads</li>
              <li>No account registration required</li>
              <li>Works entirely offline after initial page load</li>
            </ul>
            <p className={`mb-4 ${theme.text} leading-7`}>
              For more details, please see our{' '}
              <a href="/privacy" className={`${theme.link} underline`}>Privacy Policy</a>.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Part of EchoLog Ecosystem</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Proto Workbench is part of the <strong className="font-semibold">EchoLog</strong> ecosystem, a collection of tools 
              focused on accessibility and developer productivity. EchoLog's mission is to build accessible, privacy-conscious tools 
              that make both accessibility and developer workflows better.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Other tools in our ecosystem include:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>
                <a href="https://echologai.com" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                  EchoLog AI Voice Assistant
                </a> - Chrome extension for hands-free computer navigation
              </li>
              <li>
                <a href="https://jsonformatter.echologai.com" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                  JSON Formatter Pro
                </a> - Online JSON formatter, validator, and minifier
              </li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Who We Are</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              EchoLog is built by a small, focused team that cares deeply about accessibility, privacy, and developer experience. 
              We combine experience across software engineering, AI, and user experience design to create products that are both 
              powerful and approachable.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Our principles:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li><strong>Privacy by Design:</strong> We minimize data collection and keep computation on the user's device</li>
              <li><strong>Accessibility First:</strong> We design with users who rely on assistive technologies in mind</li>
              <li><strong>Developer-Friendly:</strong> Our tools are built to be simple, fast, and practical for everyday workflows</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Feedback & Support</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We're always looking to improve our tools. If you have feedback, feature requests, or encounter any issues, 
              we'd love to hear from you. You can reach us at{' '}
              <a href="mailto:echolog07@gmail.com" className={`${theme.link} underline`}>echolog07@gmail.com</a>.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              For more information about EchoLog, visit our{' '}
              <a href="https://echologai.com/about/" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                main About page
              </a>.
            </p>
          </section>
        </div>

        <div className={`mt-8 pt-6 border-t ${theme.border}`}>
          <a 
            href="/" 
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState(null, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className={`inline-flex items-center ${theme.link} hover:underline cursor-pointer`}
          >
            ← Back to Proto Workbench
          </a>
        </div>
      </div>
      <Footer darkMode={darkMode} />
    </div>
  );
};

export default AboutPage;

